import { useState, useEffect, useCallback } from 'react';
import { lectorApi } from '../api/lectorApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de lectores
 * Proporciona estado y funciones para operaciones CRUD de lectores
 */
export const useLectores = () => {
  const [lectores, setLectores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Función helper para recargar lectores manteniendo el filtrado por proyecto
   */
  const reloadLectores = useCallback(async () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    if (selectedProject && selectedProject.id) {
      const response = await lectorApi.getByProyecto(selectedProject.id);
      setLectores(response.data);
    } else {
      const response = await lectorApi.getAll();
      setLectores(response.data);
    }
  }, []);

  /**
   * Obtiene todos los lectores con filtros opcionales
   */
  const fetchLectores = useCallback(async (idProyecto = null, idComedor = null, idLector = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.getAll(idProyecto, idComedor, idLector);
      setLectores(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar lectores');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un lector específico por ID
   */
  const fetchLectorById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el lector');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo lector
   */
  const createLector = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.create(data);
      showSuccessToast('Lector creado exitosamente');
      
      // Refrescar la lista de lectores manteniendo el filtrado por proyecto
      await reloadLectores();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el lector');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadLectores]);

  /**
   * Actualiza un lector existente
   */
  const updateLector = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.update(id, data);
      showSuccessToast('Lector actualizado exitosamente');
      
      // Refrescar la lista de lectores manteniendo el filtrado por proyecto
      await reloadLectores();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el lector');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadLectores]);

  /**
   * Elimina un lector
   */
  const deleteLector = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await lectorApi.delete(id);
      showSuccessToast('Lector eliminado exitosamente');
      
      // Refrescar la lista de lectores manteniendo el filtrado por proyecto
      await reloadLectores();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el lector');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [reloadLectores]);

  /**
   * Obtiene lectores por proyecto y actualiza el estado
   */
  const fetchLectoresByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.getByProyecto(proyectoId);
      setLectores(response.data); // Actualizar el estado con los lectores filtrados
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar lectores del proyecto');
      setError(errorMessage);
      setLectores([]); // Limpiar lectores en caso de error
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene lectores por comedor
   */
  const fetchLectoresByComedor = useCallback(async (comedorId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await lectorApi.getByComedor(comedorId);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar lectores del comedor');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Nota: No se cargan lectores automáticamente.
  // Los componentes deben llamar explícitamente a fetchLectores() o fetchLectoresByProyecto()

  return {
    // Estado
    lectores,
    loading,
    error,
    
    // Funciones
    fetchLectores,
    fetchLectorById,
    createLector,
    updateLector,
    deleteLector,
    fetchLectoresByProyecto,
    fetchLectoresByComedor,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useLectores;
